// Phase 8.6B — Lifted Network Profile editor
// Wraps the existing /api/auth/profile endpoint with display name, bio,
// city/state, profile_type and business_category fields. Backed by the
// already-shipped /api/uploads endpoint for profile photo.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Loader2, User as UserIcon } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MediaUploader from "@/components/lifted/MediaUploader";

const PROFILE_TYPES = ["individual", "affiliate", "driver", "partner_store",
                       "franchisee", "brand", "business"];
const BUSINESS_CATEGORIES = ["", "head_shop", "dispensary", "lifestyle_brand",
                             "clothing_brand", "delivery_service",
                             "accessories_brand", "smoke_shop", "creator",
                             "promoter", "other"];

export default function NetworkProfile() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    display_name: "", bio: "", city: "", state: "",
    website_url: "", brand_name: "",
    profile_type: "individual", business_category: "",
  });
  const [avatarMedia, setAvatarMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!user) return;
    setForm({
      display_name: user.display_name || user.name || "",
      bio: user.bio || "",
      city: user.city || "",
      state: user.state || "",
      website_url: user.website_url || "",
      brand_name: user.brand_name || "",
      profile_type: user.profile_type || "individual",
      business_category: user.business_category || "",
    });
  }, [user?.id]);  // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const payload = { ...form };
      if (avatarMedia[0]?.url) payload.avatar_url = avatarMedia[0].url;
      await api.patch("/auth/profile", payload);
      setMsg({ type: "ok", text: "Profile saved." });
      refreshMe?.();
      setAvatarMedia([]);
    } catch (e) {
      setMsg({ type: "err", text: apiErrorMessage(e) });
    } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="network-profile-edit">
      <header>
        <div className="eyebrow">Lifted Network</div>
        <h1 className="font-anton text-3xl uppercase text-white">My profile</h1>
        <p className="text-lifted-dim text-sm mt-1">
          What ecosystem members see. Role badges come from your assigned roles only.
        </p>
      </header>

      {/* Roles */}
      <section className="glass-panel p-4" data-testid="profile-roles">
        <div className="text-[0.65rem] font-bebas tracking-[0.22em] text-lifted-dim mb-2">YOUR ROLES</div>
        <div className="flex flex-wrap gap-1.5">
          {(user.roles || []).map((r) => (
            <span key={r} className="px-2 py-1 rounded-full bg-lifted-green/15 text-lifted-green text-[0.6rem] uppercase tracking-[0.16em]">
              {r}
            </span>
          ))}
        </div>
      </section>

      {/* Avatar */}
      <section className="glass-panel p-5" data-testid="profile-avatar">
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="shrink-0">
            <div className="h-24 w-24 rounded-full overflow-hidden border-2 border-lifted-green/40 bg-black grid place-items-center">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <UserIcon size={32} className="text-lifted-green" />
              )}
            </div>
          </div>
          <div className="flex-1 w-full">
            <div className="text-[0.65rem] font-bebas tracking-[0.22em] text-lifted-dim mb-2">PROFILE PHOTO</div>
            <MediaUploader value={avatarMedia} onChange={setAvatarMedia}
              purpose="profile_image" maxFiles={1} allowVideo={false}
              primaryEnabled={false} testId="profile-avatar-uploader" />
          </div>
        </div>
      </section>

      {/* Profile fields */}
      <section className="glass-panel p-5 space-y-4" data-testid="profile-fields">
        <Field label="Display name" value={form.display_name}
               onChange={(v) => set("display_name", v)} testid="profile-display-name" />
        <Field label="Bio" value={form.bio} onChange={(v) => set("bio", v)}
               multiline testid="profile-bio" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="City" value={form.city} onChange={(v) => set("city", v)} testid="profile-city" />
          <Field label="State" value={form.state} onChange={(v) => set("state", v)} testid="profile-state" />
        </div>
        <Field label="Website" value={form.website_url}
               onChange={(v) => set("website_url", v)} testid="profile-website" />
        <Field label="Brand / store name (if applicable)" value={form.brand_name}
               onChange={(v) => set("brand_name", v)} testid="profile-brand-name" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Select label="Profile type" value={form.profile_type} options={PROFILE_TYPES}
                  onChange={(v) => set("profile_type", v)} testid="profile-type" />
          <Select label="Business category" value={form.business_category}
                  options={BUSINESS_CATEGORIES}
                  onChange={(v) => set("business_category", v)} testid="profile-category" />
        </div>
      </section>

      {msg && (
        <div className={`text-sm ${msg.type === "ok" ? "text-lifted-green" : "text-rose-300"}`}
             data-testid="profile-msg">{msg.text}</div>
      )}
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className="btn-lifted inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="profile-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {busy ? "Saving…" : "Save profile"}
        </button>
        <button onClick={() => nav("/network")} className="btn-ghost" data-testid="profile-cancel">
          Back to Network
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline, testid }) {
  return (
    <div>
      <div className="text-[0.65rem] font-bebas tracking-[0.22em] text-lifted-dim mb-1">{label.toUpperCase()}</div>
      {multiline ? (
        <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-lifted-text focus:border-lifted-green/60 focus:outline-none"
          data-testid={testid} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-lifted-text focus:border-lifted-green/60 focus:outline-none"
          data-testid={testid} />
      )}
    </div>
  );
}

function Select({ label, value, options, onChange, testid }) {
  return (
    <div>
      <div className="text-[0.65rem] font-bebas tracking-[0.22em] text-lifted-dim mb-1">{label.toUpperCase()}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-lifted-text focus:border-lifted-green/60 focus:outline-none"
        data-testid={testid}>
        {options.map((o) => (
          <option key={o} value={o}>{o || "—"}</option>
        ))}
      </select>
    </div>
  );
}
